import type { Bi } from 'schools/lalKitab/types/bilingual';

export type LalKitabTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type LalKitabChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: LalKitabTopic[];
};

export const LAL_KITAB_HERO = {
  eyebrow: {
    en: 'Remedial astrology · Lal Kitab',
    hi: 'उपचारात्मक ज्योतिष · लाल किताब',
  },
  titleShort: { en: 'Lal Kitab', hi: 'लाल किताब' },
  title: { en: 'Lal Kitab Astrology', hi: 'लाल किताब ज्योतिष' },
  motto: {
    en: 'Karmic debts, fixed houses, and practical Upayas - remedies over expensive gems.',
    hi: 'कर्म ऋण, स्थिर भाव और व्यावहारिक उपाय - महँगे रत्नों से अधिक उपचार।',
  },
  tagline: {
    en: 'Master Lal Kitab from Roop Chand Joshi\'s philosophy through Ghar Kaim, astro-palmistry, artificial planets, Pitri Rina, Varshphal, and safe 43-day remedies.',
    hi: 'रूप चंद जोशी के दर्शन से घर कायम, हस्त-ज्योतिष, मसनुई ग्रह, पितृ ऋण, वर्षफल और सुरक्षित 43-दिवसीय उपाय तक लाल किताब सीखें।',
  },
  intro: {
    en: 'Learn Lal Kitab step by step: the fixed Aries-ascendant house system, sleeping and awakened planets, ancestral debt identification, annual chart timing, and ethical remedy prescription - built for students moving from classical Vedic to Lal Kitab\'s unique remedial lens.',
    hi: 'लाल किताब चरणबद्ध सीखें: स्थिर मेष लग्न भाव व्यवस्था, सोते-जागते ग्रह, पूर्वज ऋण पहचान, वार्षिक कुंडली समय और नैतिक उपाय - शास्त्रीय वैदिक से लाल किताब के विशिष्ट उपचार दृष्टिकोण की ओर।',
  },
  trait: {
    en: 'Upaya driven · karmic remedy',
    hi: 'उपाय आधारित · कर्म उपचार',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): LalKitabTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const LAL_KITAB_CHAPTERS: LalKitabChapter[] = [
  {
    id: 'week1-intro',
    number: 1,
    title: { en: 'Intro & Philosophy', hi: 'परिचय और दर्शन' },
    subtitle: {
      en: 'Module 1 · Origin of Roop Chand Joshi, karmic blueprint, and the no-gemstone rule',
      hi: 'मॉड्यूल 1 · रूप चंद जोशी की उत्पत्ति, कर्म नक्शा और रत्न-मंत्र निषेध',
    },
    icon: '📕',
    topics: [
      topic('history-roop-chand', 'Roop Chand Joshi', 'रूप चंद जोशी', 'Origin story and the five original Lal Kitab books.', 'उत्पत्ति कथा और पाँच मूल लाल किताब पुस्तकें।', 18, '☰'),
      topic('karmic-blueprint', 'Karmic Blueprint', 'कर्म नक्शा', 'How Lal Kitab reads charts as karmic debt ledgers focused on Upayas.', 'लाल किताब कुंडली को उपाय-केंद्रित कर्म ऋण खाते के रूप में कैसे पढ़ती है।', 17, '⚖'),
      topic('no-gemstones-mantras', 'No Gems or Mantras', 'रत्न-मंत्र निषेध', 'Why expensive gemstones and mantras are forbidden in Lal Kitab.', 'लाल किताब में महँगे रत्न और मंत्र क्यों वर्जित हैं।', 16, '⊘'),
    ],
  },
  {
    id: 'week2-fixed-houses',
    number: 2,
    title: { en: 'Fixed Houses', hi: 'स्थिर भाव' },
    subtitle: {
      en: 'Module 1 · Converting Vedic charts to Ghar Kaim and Pakka Ghar lords',
      hi: 'मॉड्यूल 1 · वैदिक कुंडली को घर कायम और पक्का घर स्वामी में बदलना',
    },
    icon: '⌂',
    topics: [
      topic('vedic-to-lalkitab-chart', 'Vedic to Lal Kitab', 'वैदिक से लाल किताब', 'Re-reading the same birth chart through Lal Kitab\'s fixed house lens.', 'स्थिर भाव दृष्टि से वही जन्म कुंडली पुनः पढ़ना।', 18, '⇄'),
      topic('aries-ascendant-rule', 'Aries Ascendant Rule', 'मेष लग्न नियम', 'House 1 is always Aries, House 2 Taurus - the fixed sign mapping.', 'भाव 1 सदा मेष, भाव 2 वृषभ - स्थिर राशि मानचित्रण।', 19, '♈'),
      topic('pakka-ghar-lords', 'Pakka Ghar Lords', 'पक्का घर स्वामी', 'Memorizing the permanent lords of all twelve houses.', 'बारह भावों के स्थायी स्वामी याद करना।', 18, '▣'),
    ],
  },
  {
    id: 'week3-astro-palmistry',
    number: 3,
    title: { en: 'Astro-Palmistry', hi: 'हस्त-ज्योतिष' },
    subtitle: {
      en: 'Module 1 · Mapping twelve houses onto the palm and verifying birth time',
      hi: 'मॉड्यूल 1 · बारह भावों को हथेली पर मानचित्रण और जन्म समय सत्यापन',
    },
    icon: '✋',
    topics: [
      topic('hand-chart-link', 'Hand-Chart Link', 'हाथ-कुंडली संबंध', 'The twelve houses mapped directly onto regions of the palm.', 'बारह भाव सीधे हथेली के क्षेत्रों पर मानचित्रित।', 17, '🔗'),
      topic('mounts-and-planets', 'Mounts & Planets', 'पर्वत और ग्रह', 'How planetary strength reflects on mounts and lines of the hand.', 'ग्रह बल हथेली के पर्वत और रेखाओं पर कैसे दिखता है।', 18, '⛰'),
      topic('palm-birthtime-verify', 'Palm Birth-Time Check', 'हस्त जन्म समय जाँच', 'Using palm lines to verify or correct an incorrect birth time.', 'अशुद्ध जन्म समय सत्यापित या सुधारने हेतु हस्त रेखाएँ।', 17, '⌚'),
    ],
  },
  {
    id: 'week4-artificial-planets',
    number: 4,
    title: { en: 'Artificial Planets', hi: 'मसनुई ग्रह' },
    subtitle: {
      en: 'Module 2 · Friend-enemy matrix, Masnui Grah blends, and new exaltation rules',
      hi: 'मॉड्यूल 2 · मित्र-शत्रु मैट्रिक्स, मसनुई ग्रह मिश्रण और नए उच्च नियम',
    },
    icon: '⊕',
    topics: [
      topic('friend-enemy-matrix', 'Friend-Enemy Matrix', 'मित्र-शत्रु मैट्रिक्स', 'Lal Kitab\'s unique natural friend and enemy planet relationships.', 'लाल किताब के विशिष्ट स्वाभाविक मित्र और शत्रु ग्रह संबंध।', 18, '⚔'),
      topic('masnui-grah-blends', 'Masnui Grah Blends', 'मसनुई ग्रह मिश्रण', 'How two planets together create a third artificial planet.', 'दो ग्रह मिलकर तीसरा मसनुई ग्रह कैसे बनाते हैं।', 19, '⚗'),
      topic('exaltation-debilitation', 'Exalt & Debility', 'उच्च और नीच', 'New Lal Kitab rules for planetary strength beyond classical dignity.', 'शास्त्रीय गरिमा से परे लाल किताब के नए ग्रह बल नियम।', 17, '↕'),
    ],
  },
  {
    id: 'week5-sleeping-awakened',
    number: 5,
    title: { en: 'Sleeping Planets', hi: 'सोते ग्रह' },
    subtitle: {
      en: 'Module 2 · Dormancy rule, activation triggers, and natural wake-up timing',
      hi: 'मॉड्यूल 2 · निष्क्रियता नियम, सक्रियण ट्रिगर और स्वाभाविक जागरण समय',
    },
    icon: '☽',
    topics: [
      topic('dormancy-rule', 'Dormancy Rule', 'निष्क्रियता नियम', 'Identifying sleeping houses and sleeping planets in the chart.', 'कुंडली में सोते भाव और सोते ग्रह पहचानना।', 18, '💤'),
      topic('activation-trigger', 'Activation Triggers', 'सक्रियण ट्रिगर', 'Which planet or house wakes up another through transit or dasha.', 'कौन सा ग्रह या भाव गोचर या दशा से दूसरे को जगाता है।', 17, '⚡'),
      topic('sleeping-planet-timing', 'Wake-Up Timing', 'जागरण समय', 'When a sleeping planet naturally awakens in a person\'s life.', 'सोता ग्रह व्यक्ति के जीवन में स्वाभाविक रूप से कब जागता है।', 18, '⏰'),
    ],
  },
  {
    id: 'week6-special-charts',
    number: 6,
    title: { en: 'Special Charts', hi: 'विशेष कुंडली' },
    subtitle: {
      en: 'Module 2 · Andha, Dharmi, and Teewa chart classifications',
      hi: 'मॉड्यूल 2 · अंधा, धर्मी और तीवा कुंडली वर्गीकरण',
    },
    icon: '◈',
    topics: [
      topic('andha-kundli', 'Andha Kundli', 'अंधा कुंडली', 'Blind chart - major life struggles through 4th-house afflictions.', 'अंधा कुंडली - चतुर्थ भाव पीड़ा से बड़े जीवन संघर्ष।', 18, '⊗'),
      topic('dharmi-kundli', 'Dharmi Kundli', 'धर्मी कुंडली', 'Pious chart - divine protective shields against crises.', 'धर्मी कुंडली - संकटों से दिव्य सुरक्षा ढाल।', 17, '☯'),
      topic('teewa-charts', 'Teewa Charts', 'तीवा कुंडली', 'Half charts - differentiating normal from highly volatile charts.', 'तीवा कुंडली - सामान्य और अत्यंत अस्थिर कुंडली में अंतर।', 18, '½'),
    ],
  },
  {
    id: 'week7-pitri-rina',
    number: 7,
    title: { en: 'Pitri Rina', hi: 'पितृ ऋण' },
    subtitle: {
      en: 'Module 2 · Ancestral debts, curse identification, and real-world symptoms',
      hi: 'मॉड्यूल 2 · पूर्वज ऋण, श्राप पहचान और वास्तविक लक्षण',
    },
    icon: '⛓',
    topics: [
      topic('identifying-curse', 'Identifying the Curse', 'श्राप पहचान', 'Spotting karmic debt through specific 4th and 5th house placements.', 'चतुर्थ और पंचम भाव स्थिति से कर्म ऋण पहचानना।', 18, '⚠'),
      topic('types-of-debts', 'Types of Debts', 'ऋण के प्रकार', 'Forefather, self, mother, and female ancestral debt categories.', 'पितृ, स्व, मातृ और स्त्री पूर्वज ऋण श्रेणियाँ।', 19, '§'),
      topic('debt-symptoms', 'Debt Symptoms', 'ऋण के लक्षण', 'Real-world signs - sudden ruin, health patterns, repeated setbacks.', 'वास्तविक संकेत - अचानक पतन, स्वास्थ्य पैटर्न, बार-बार असफलता।', 17, '✦'),
    ],
  },
  {
    id: 'week8-varshphal',
    number: 8,
    title: { en: 'Varshphal', hi: 'वर्षफल' },
    subtitle: {
      en: 'Module 3 · Annual birthday chart, monthly pressure, and transit overlay',
      hi: 'मॉड्यूल 3 · वार्षिक जन्मदिन कुंडली, मासिक दबाव और गोचर अध्यारोप',
    },
    icon: '🎂',
    topics: [
      topic('birthday-chart-mutation', 'Birthday Chart', 'जन्मदिन कुंडली', 'How the Lal Kitab chart mutates every year on the birthday.', 'जन्मदिन पर लाल किताब कुंडली हर वर्ष कैसे बदलती है।', 18, '↻'),
      topic('predictive-months', 'Predictive Months', 'भविष्यवाणी माह', 'Pinpointing which months face planetary pressure in the year.', 'वर्ष में किन महीनों में ग्रह दबाव आएगा यह निर्धारित करना।', 17, '📅'),
      topic('varshphal-transits', 'Varshphal Transits', 'वर्षफल गोचर', 'Overlaying the annual chart onto the natal birth chart.', 'वार्षिक कुंडली को जन्म कुंडली पर अध्यारोपित करना।', 18, '⊞'),
    ],
  },
  {
    id: 'week9-upaya-philosophy',
    number: 9,
    title: { en: 'Upaya Philosophy', hi: 'उपाय दर्शन' },
    subtitle: {
      en: 'Module 3 · Remedy logic, backfire warnings, and the 43-day rule',
      hi: 'मॉड्यूल 3 · उपाय तर्क, उलटा असर चेतावनी और 43-दिवस नियम',
    },
    icon: '🛠',
    topics: [
      topic('remedy-logic', 'Remedy Logic', 'उपाय तर्क', 'How shifting objects and habits alters planetary energy.', 'वस्तुएँ और आदतें बदलकर ग्रह ऊर्जा कैसे बदलती है।', 18, '⚙'),
      topic('backfire-warnings', 'Backfire Warnings', 'उलटा असर चेतावनी', 'When a remedy can backfire - timing, secrecy, and wrong items.', 'उपाय उलटा कब पड़ सकता है - समय, गोपनीयता और गलत वस्तु।', 17, '⚠'),
      topic('43-day-rule', '43-Day Rule', '43-दिवस नियम', 'The mandatory continuous 43-day cycle for standard remedies.', 'मानक उपायों के लिए अनिवार्य निरंतर 43-दिवस चक्र।', 16, '43'),
    ],
  },
  {
    id: 'week10-case-studies-1',
    number: 10,
    title: { en: 'Case Studies I', hi: 'केस अध्ययन I' },
    subtitle: {
      en: 'Module 3 · Career, money blocks, and relationship delay analysis',
      hi: 'मॉड्यूल 3 · करियर, धन बाधा और संबंध विलंब विश्लेषण',
    },
    icon: '💼',
    topics: [
      topic('career-money-blocks', 'Career & Money', 'करियर और धन', 'Reading charts for job losses and financial blockages.', 'नौकरी हानि और वित्तीय बाधाओं के लिए कुंडली पढ़ना।', 19, '₹'),
      topic('relationship-blocks', 'Relationship Blocks', 'संबंध बाधाएँ', 'Afflicted Venus/Mars combinations causing marriage delays.', 'विवाह विलंब करने वाले पीड़ित शुक्र/मंगल संयोजन।', 18, '♥'),
    ],
  },
  {
    id: 'week11-case-studies-2',
    number: 11,
    title: { en: 'Case Studies II', hi: 'केस अध्ययन II' },
    subtitle: {
      en: 'Module 3 · Health, progeny, and live chart diagnosis practice',
      hi: 'मॉड्यूल 3 · स्वास्थ्य, संतान और सजीव कुंडली निदान अभ्यास',
    },
    icon: '🔍',
    topics: [
      topic('health-progeny', 'Health & Progeny', 'स्वास्थ्य और संतान', 'Analyzing charts for chronic illness and childbirth issues.', 'दीर्घ रोग और संतान समस्याओं के लिए कुंडली विश्लेषण।', 19, '⚕'),
      topic('live-practice-diagnosis', 'Live Diagnosis', 'सजीव निदान', 'Step-by-step live decoding of blank charts in class.', 'कक्षा में खाली कुंडली का चरणबद्ध सजीव विश्लेषण।', 20, '◎'),
    ],
  },
  {
    id: 'week12-ethics-graduation',
    number: 12,
    title: { en: 'Ethics & Graduation', hi: 'नैतिकता और स्नातक' },
    subtitle: {
      en: 'Module 3 · Astrologer\'s code, final exam, and certification wrap-up',
      hi: 'मॉड्यूल 3 · ज्योतिषी संहिता, अंतिम परीक्षा और प्रमाणन समापन',
    },
    icon: '🎓',
    topics: [
      topic('astrologers-code', 'Astrologer\'s Code', 'ज्योतिषी संहिता', 'Ethical boundaries and psychological counselling basics.', 'नैतिक सीमाएँ और मनोवैज्ञानिक परामर्श आधार।', 17, '⚖'),
      topic('final-exam', 'Final Exam', 'अंतिम परीक्षा', 'Live interactive chart decoding session as course assessment.', 'पाठ्यक्रम मूल्यांकन के रूप में सजीव कुंडली विश्लेषण।', 18, '✓'),
      topic('certification-wrapup', 'Certification', 'प्रमाणन', 'Course wrap-up, completion criteria, and next steps.', 'पाठ्यक्रम समापन, पूर्णता मानदंड और आगे के कदम।', 16, '🏅'),
    ],
  },
];

const topicById = new Map<string, { topic: LalKitabTopic; chapter: LalKitabChapter }>();

for (const chapter of LAL_KITAB_CHAPTERS) {
  for (const t of chapter.topics) {
    topicById.set(t.id, { topic: t, chapter });
  }
}

export function getLalKitabTopic(id: string) {
  return topicById.get(id);
}

export function getLalKitabChapter(chapterId: string): LalKitabChapter | undefined {
  return LAL_KITAB_CHAPTERS.find((c) => c.id === chapterId);
}

export function getLalKitabChapterIndex(chapterId: string): number {
  return LAL_KITAB_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getLalKitabChapterNeighbors(chapterId: string): {
  prev: LalKitabChapter | null;
  next: LalKitabChapter | null;
} {
  const idx = getLalKitabChapterIndex(chapterId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? LAL_KITAB_CHAPTERS[idx - 1] : null,
    next: idx < LAL_KITAB_CHAPTERS.length - 1 ? LAL_KITAB_CHAPTERS[idx + 1] : null,
  };
}

export function getLalKitabChapterReadMinutes(chapter: LalKitabChapter): number {
  return chapter.topics.reduce((sum, t) => sum + t.readMinutes, 0);
}

export function getAllLalKitabTopics(): LalKitabTopic[] {
  return LAL_KITAB_CHAPTERS.flatMap((c) => c.topics);
}

export function getLalKitabTopicCount(): number {
  return getAllLalKitabTopics().length;
}

const flatTopicOrder = LAL_KITAB_CHAPTERS.flatMap((c) => c.topics);

export function getLalKitabTopicNeighbors(topicId: string): {
  prev: LalKitabTopic | null;
  next: LalKitabTopic | null;
} {
  const idx = flatTopicOrder.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? flatTopicOrder[idx - 1] : null,
    next: idx < flatTopicOrder.length - 1 ? flatTopicOrder[idx + 1] : null,
  };
}
